import _ from 'lodash'
import classNames from 'classnames'
import { html, css, LitElement } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { type VirtualizerHostElement, virtualize, virtualizerRef } from '@lit-labs/virtualizer/virtualize.js'
import '@lit-labs/virtualizer'
import '@lit-labs/virtualizer/layouts/flow.js'
import {
    getAssigneeTeamNames,
    getTeamCountText,
    isTeam,
    type Assignable,
    type User,
} from '../../shared/domain/schedule/assignee.no-deps'
import type { TeamWithCount } from '../domain/teams'
import type { SupportedLanguage } from '../i18n/i18n.no-deps'
import { noSearchResults } from './helpers/noSearchResults'
import { isUserMemberOfASelectedTeam } from './helpers/user-list-helpers'
import './zaptic-assignable-icon'
import './zaptic-checkbox'

export enum Events {
    onHovered = 'onHovered',
    onSelected = 'onSelected',
    onLayoutComplete = 'onLayoutComplete',
}

interface CustomEventPayload {
    [Events.onHovered]: number
    [Events.onSelected]: number
    [Events.onLayoutComplete]: boolean
}

export interface CustomEvents {
    [Events.onHovered]: CustomEvent<CustomEventPayload['onHovered']>
    [Events.onSelected]: CustomEvent<CustomEventPayload['onSelected']>
    [Events.onLayoutComplete]: CustomEvent<CustomEventPayload['onLayoutComplete']>
}

@customElement('zaptic-user-list')
export class UserList extends LitElement {
    public static tagName = 'zaptic-user-list'

    static styles = css`
        :host {
            height: 0;
            overflow: visible;
            height: 100%;
        }

        .above {
            transform: translate(0, -100%);
        }

        .user-list-container {
            height: 100%;
            max-height: var(--UserList-max-height, 250px);
            overflow-y: scroll;
        }

        .user-list {
            position: relative;
            margin: 0;
            padding: 0;
        }

        .list-item {
            display: flex;
            align-items: center;
            font-weight: var(--font-weight-medium);
            font-size: 14px;
            padding: 10px 15px;
            border-radius: 5px;
            cursor: pointer;
            width: 100%;

            &.item-small {
                --ProfileCircle-size-override: 32px;
                height: 32px;
                padding: 8px 15px;
            }
        }
        .item-focus {
            background: var(--disabled);
        }
        .item-circle {
            margin-right: 6px;
        }

        .item-disabled {
            pointer-events: none;
        }

        .not-found {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 25px 45px;
            height: 100%;
            max-height: 200px;
        }
        .not-found-text {
            padding-bottom: 15px;
            color: #b2c3db;
            font-weight: var(--font-weight-medium);
            font-size: 14px;
        }

        .item-details {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            line-height: normal;
        }

        .item-secondary-text {
            color: var(--grey3);

            .item-large & {
                margin-top: 2px;
            }
        }
    `

    public scrollSelectedIntoView() {
        const selected = (this.shadowRoot.querySelector('.item-focus') ||
            this.shadowRoot.querySelector('.list-item')) as HTMLElement
        selected?.scrollIntoView?.({ block: 'nearest' })
    }

    updated(changedProperties: Map<string, any>) {
        if (changedProperties.has('selected')) {
            this.scrollSelectedIntoView()
        }
    }

    async firstUpdated() {
        // Wait until lit-virtualizer is done rendering the initial list to scroll into the
        // initial focused item (if any).
        await this.layoutComplete.catch((error) => {
            if (error === 'disconnected') {
                // Mysterious 'disconnected' error messes up Portal's React unit tests. Let's catch it.
                // eslint-disable-next-line no-console
                console.error('zaptic-user-list layoutComplete error', error)
                return
            }

            throw error
        })

        requestAnimationFrame(() => {
            // Fixes storybook tests opening the AssigneeSelect component in a popover and
            // the user list being empty. A scroll event forces the list to be rendered.
            this._scrollerElement.dispatchEvent(new Event('scroll'))
            this.dispatchEvent(new CustomEvent(Events.onLayoutComplete, { detail: true }))
        })

        this.scrollSelectedIntoView()
    }

    @property({ attribute: 'selected', converter: (value) => JSON.parse(value) })
    selected: number

    @property({ attribute: 'items', converter: (value) => JSON.parse(value) })
    items: Assignable[] = []

    @property({ attribute: 'language', converter: (value) => JSON.parse(value) })
    language: SupportedLanguage

    @property({ attribute: 'multi-select-options', converter: { fromAttribute: (value) => JSON.parse(value) } })
    'multi-select-options': { allTeamsSelected: boolean } = null

    @property({ attribute: 'selected-items', converter: (value) => JSON.parse(value) })
    'selected-items': Assignable[] = []

    @property({ attribute: 'use-jobtitle-as-user-subtitle', converter: (value) => JSON.parse(value) })
    'use-jobtitle-as-user-subtitle' = false

    @property({ attribute: 'item-size', converter: (value) => JSON.parse(value) })
    'item-size': 'small' | 'large' = 'large'

    @property({ converter: (value) => JSON.parse(value) })
    disabled = false

    @query('.user-list-container')
    _scrollerElement: HTMLUListElement

    private handleHover(index: number) {
        return () => {
            this.dispatchEvent(new CustomEvent('onHovered', { detail: index }))
        }
    }

    private handleClick(index: number) {
        return () => {
            this.dispatchEvent(new CustomEvent('onSelected', { detail: index }))
        }
    }

    private isItemSelected = (item: Assignable) => {
        return this['selected-items'] ? _.some(this['selected-items'], item) : false
    }

    private getUserItemSubtitle(user: User) {
        if (this['use-jobtitle-as-user-subtitle']) {
            return user.jobTitle
        }
        return getAssigneeTeamNames(this.language, user)
    }

    private itemHtml(item: Assignable, index: number, selected: boolean, selectedTeams: TeamWithCount[]) {
        const isDisabled = isUserMemberOfASelectedTeam(item, selectedTeams) || this.disabled

        // TODO: At the moment this user-list displays in three ways depending on if it is a user, team or tag. Ideally it
        // should be split into two/three components that extend a common base.
        const itemHtml = html`
            <zaptic-assignable-icon class="item-circle" .assignee=${item} title="${item.name}"></zaptic-assignable-icon>
            <div class="item-details">
                <span class="item-primary-text">${item.name}</span>
                ${this['multi-select-options'] !== null
                    ? html`<div class="item-secondary-text">
                          ${isTeam(item) ? getTeamCountText(this.language, item) : this.getUserItemSubtitle(item)}
                      </div>`
                    : ''}
            </div>
        `
        return html`
            <li
                class=${classNames('list-item', {
                    'item-focus': selected,
                    'item-disabled': isDisabled,
                    'item-large': this['item-size'] === 'large',
                    'item-small': this['item-size'] === 'small',
                })}
                @mouseover=${this.handleHover(index)}
                @focus=${this.handleHover(index)}
                @click=${isDisabled ? () => {} : this.handleClick(index)}
                tabindex="-1"
            >
                <zaptic-checkbox
                    .selected=${this.isItemSelected(item)}
                    .hide=${this['multi-select-options'] === null}
                    ?disabled=${isDisabled}
                    @onChange=${null}
                ></zaptic-checkbox>
                ${itemHtml}
                </div>
            </li>
        `
    }

    private itemListHtml(items: Assignable[], selectedIndex: number) {
        const selectedTeams = this['selected-items'].filter(isTeam)
        return html`
            <div class="user-list-container">
                <ul class="user-list">
                    ${virtualize({
                        items,
                        renderItem: (item: Assignable, idx: number) =>
                            this.itemHtml(item, idx, selectedIndex === idx, selectedTeams),
                    })}
                </ul>
            </div>
        `
    }

    get virtualizer() {
        const virtualizerHost: VirtualizerHostElement = this.shadowRoot!.querySelector('.user-list')
        return virtualizerHost[virtualizerRef]
    }

    get layoutComplete() {
        return this.virtualizer.layoutComplete
    }

    render() {
        if (this.items == null || this.language == null || this.selected == null) {
            return
        }

        return this.items.length === 0 ? noSearchResults(this.language) : this.itemListHtml(this.items, this.selected)
    }
}
